/**
 * SideMainVideo: SIDE 모드 메인 영상 영역.
 *
 * v1 원본: RenewalComponents/SVContent/SideScreen/MainVideo/
 * v2 변환: 단일 함수형 컴포넌트
 *
 * - 메인 참여자의 큰 비디오
 * - 확장/축소 토글 (서브 리스트 접기)
 * - 클릭 → GRID 모드 전환
 * - Phase 7에서 화이트보드 감지 추가 예정
 */
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { uiActions } from '@/store/slices/uiSlice';
import { whiteboardActions } from '@/store/slices/whiteboardSlice';
import VideoPanel from '../SubVideo/VideoPanel';
import type { Participant } from '@/store/slices/participantsSlice';
import { isRecorderMode } from '@/utils/recorderMode';

interface SideMainVideoProps {
  localUserId: string;
  mainUserId: string | null;
  isExpand: boolean;
  onExpand: () => void;
  onCollapse: () => void;
  hasOtherUsers: boolean;
}

const SideMainVideo: React.FC<SideMainVideoProps> = ({
  localUserId,
  mainUserId,
  isExpand,
  onExpand,
  onCollapse,
  hasOtherUsers,
}) => {
  const dispatch = useAppDispatch();
  const participants = useAppSelector((s) => s.participants);
  const whiteboard = useAppSelector((s) => s.whiteboard);
  const screenShare = useAppSelector((s) => s.screenShare);
  const pinnedShowScreenShare = useAppSelector((s) => s.mainUser.pinnedShowScreenShare);

  const isCoachMarkActive =
    whiteboard.isOpen &&
    whiteboard.isSketchMode &&
    whiteboard.isOwner &&
    !whiteboard.isSketchCoachMarkShown;

  const handleCloseCoachMark = useCallback(() => {
    dispatch(whiteboardActions.setSketchCoachMarkShown(true));
  }, [dispatch]);

  // type5 ("공유취소 버튼") 코치마크를 실제 버튼 위치에 동적 배치
  const overlayRef = useRef<HTMLDivElement>(null);
  const [type5Right, setType5Right] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (!isCoachMarkActive) return;

    const updatePosition = () => {
      const shareBtn = document.querySelector<HTMLElement>(
        '.newHeader_btnItem:has(.icoNoShare)'
      );
      const overlay = overlayRef.current;
      if (!shareBtn || !overlay) return;

      const btnRect = shareBtn.getBoundingClientRect();
      const overlayRect = overlay.getBoundingClientRect();
      const btnCenterX = btnRect.left + btnRect.width / 2;
      // 화살표 아이콘(39px)의 중심이 버튼 중심에 오도록 계산
      setType5Right(overlayRect.right - btnCenterX - 19.5);
    };

    // DOM 렌더 후 위치 계산
    requestAnimationFrame(updatePosition);

    window.addEventListener('resize', updatePosition);

    // 사이드바 열림/닫힘 → 오버레이·푸터 크기 변화 감지
    const observer = new ResizeObserver(updatePosition);
    if (overlayRef.current) observer.observe(overlayRef.current);
    const footer = document.querySelector('.newHeader');
    if (footer) observer.observe(footer);

    return () => {
      window.removeEventListener('resize', updatePosition);
      observer.disconnect();
    };
  }, [isCoachMarkActive]);

  const handleClickMain = useCallback(() => {
    dispatch(uiActions.setScreenMode('GRID'));
  }, [dispatch]);

  // 메인 유저 결정
  // recorder 모드: 자기 자신은 메인으로 렌더하지 않는다. 유효한 원격 메인이 있으면 그것만,
  // 없으면 아무것도 렌더하지 않는다(빈 화면).
  const recorder = isRecorderMode();
  const remoteMain = mainUserId && mainUserId !== localUserId
    ? participants.participants[mainUserId] ?? null
    : null;
  const isLocal = !recorder && (!mainUserId || mainUserId === localUserId);
  const participant: Participant | 'local' | null = isLocal
    ? 'local'
    : remoteMain ?? (recorder ? null : 'local');
  
  const expendArea = 
    <div className="sideView__funcBox" style={{zIndex: 1200}}>
    {isExpand ? (
      <button type="button" className="sideView__btnFold icoCollapse" onClick={onCollapse}>
        <span className="blind">접기</span>
      </button>
    ) : (
      <button type="button" className="sideView__btnFold icoExpand" onClick={onExpand}>
        <span className="blind">펼치기</span>
      </button>
    )}
  </div>

  // 메인 유저가 화면 공유 중인지 확인 (로컬 + 리모트 모두)
  // pinnedShowScreenShare가 명시적으로 설정된 경우 그 값을 우선 사용
  const autoScreenSharing = isLocal
    ? screenShare.isSharing
    : participant !== null && participant !== 'local' && (participant as Participant).tracks.isScreenSharing;
  const isMainUserScreenSharing = pinnedShowScreenShare !== null ? pinnedShowScreenShare : autoScreenSharing;

  return (
    <div className="sideView_wrap" style={{position:isCoachMarkActive ? "static" : "relative"}}>
      {isCoachMarkActive && (
        <div ref={overlayRef} className="rtc_coachMark__view" onClick={handleCloseCoachMark}>
          <div className="rtc_coachMark__guideBox type1">
            <span className="rtc_coachMark__guideIcon"></span>
            <p className="rtc_coachMark__guideText">스케치 화면을<br/>더 넓게 활용 해보세요</p>
          </div>
          <div className="rtc_coachMark__guideBox type2">
            <span className="rtc_coachMark__guideIcon"></span>
            <p className="rtc_coachMark__guideText">다양한 그리기 도구를 이용해<br/>화면에 스케치를 할 수 있어요!</p>
          </div>
          <div className="rtc_coachMark__guideBox type3">
            <span className="rtc_coachMark__guideIcon"></span>
            <p className="rtc_coachMark__guideText">스케치 공유 중에도<br/>다른 참석자들을 확인 할 수 있어요</p>
          </div>
          <div className="rtc_coachMark__guideBox type4">
            <span className="rtc_coachMark__guideIcon"></span>
            <p className="rtc_coachMark__guideText">작성중인 스케치 화면을 캡쳐해보세요!<br/><em>캡쳐된 이미지는 회의기록에서도 확인</em>할 수 있어요</p>
          </div>
          <div
            className="rtc_coachMark__guideBox type5"
            style={type5Right !== null ? { right: type5Right, marginRight: 0 } : undefined}
          >
            <span className="rtc_coachMark__guideIcon"></span>
            <p className="rtc_coachMark__guideText">스케치를 종료 하려면<br/>공유취소 버튼을 눌러주세요</p>
          </div>
        </div>
      )}
      {participant !== null && (
        <VideoPanel
          participant={participant}
          localUserId={localUserId}
          isMain={true}
          onSelect={handleClickMain}
          expendArea={hasOtherUsers ? expendArea : undefined}
          showScreenShare={isMainUserScreenSharing}
        />
      )}
    </div>
  );
};

export default SideMainVideo;
